import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Mail, CheckCircle, Eye, MousePointer, AlertTriangle } from 'lucide-react';
import { fetchEmailAnalytics } from '../../lib/adminApi';

interface Summary {
  total_sent: number;
  delivery_rate: number;
  open_rate: number;
  click_rate: number;
  bounce_rate: number;
  complaint_rate: number;
}

interface ByType {
  email_type: string;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  open_rate: number;
  click_rate: number;
}

interface Bounce {
  member_id: string;
  email: string;
  bounce_reason: string;
  bounced_at: string;
}

interface DailySend {
  date: string;
  count: number;
}

interface AnalyticsData {
  summary: Summary;
  by_type: ByType[];
  recent_bounces: Bounce[];
  daily_sends: DailySend[];
}

function pct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

function MetricCard({ label, value, sub, icon: Icon, color }: {
  label: string; value: string; sub?: string; icon: React.ElementType; color: string;
}) {
  return (
    <div className="bg-slate-800 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <span className="text-sm text-slate-400">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white tabular-nums">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function AdminEmailAnalytics() {
  const { data, isLoading } = useQuery<AnalyticsData>({
    queryKey: ['admin', 'email-analytics'],
    queryFn: fetchEmailAnalytics,
  });

  if (isLoading || !data) {
    return <div className="text-slate-400 py-12 text-center">Loading email analytics...</div>;
  }

  const { summary, by_type, recent_bounces, daily_sends } = data;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white font-display">Email Analytics</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard label="Total Sent" value={summary.total_sent.toLocaleString()} icon={Mail} color="bg-blue-600" />
        <MetricCard label="Delivery Rate" value={pct(summary.delivery_rate)} icon={CheckCircle} color="bg-emerald-600" />
        <MetricCard label="Open Rate" value={pct(summary.open_rate)} icon={Eye} color="bg-purple-600" />
        <MetricCard label="Click Rate" value={pct(summary.click_rate)} icon={MousePointer} color="bg-amber-600" />
        <MetricCard label="Bounce Rate" value={pct(summary.bounce_rate)} sub={summary.complaint_rate > 0 ? `${pct(summary.complaint_rate)} complaints` : undefined} icon={AlertTriangle} color="bg-red-600" />
      </div>

      {/* Daily Sends Chart */}
      <div className="bg-slate-800 rounded-xl p-5">
        <h2 className="text-lg font-bold text-white mb-4">Daily Sends (Last 30 Days)</h2>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={daily_sends}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(d: string) => d.slice(5)} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} labelStyle={{ color: '#94a3b8' }} itemStyle={{ color: '#818cf8' }} />
            <Bar dataKey="count" fill="#818cf8" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* By Type Table */}
      <div className="bg-slate-800 rounded-xl p-5">
        <h2 className="text-lg font-bold text-white mb-4">Performance by Email Type</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-400 border-b border-slate-700">
                <th className="text-left py-2 px-3">Type</th>
                <th className="text-right py-2 px-3">Sent</th>
                <th className="text-right py-2 px-3">Delivered</th>
                <th className="text-right py-2 px-3">Opened</th>
                <th className="text-right py-2 px-3">Clicked</th>
                <th className="text-right py-2 px-3">Bounced</th>
                <th className="text-right py-2 px-3">Open Rate</th>
                <th className="text-right py-2 px-3">Click Rate</th>
              </tr>
            </thead>
            <tbody>
              {by_type.map((row) => (
                <tr key={row.email_type} className="border-b border-slate-700/50 text-slate-300">
                  <td className="py-2 px-3 font-medium">{row.email_type}</td>
                  <td className="text-right py-2 px-3 tabular-nums">{row.sent}</td>
                  <td className="text-right py-2 px-3 tabular-nums">{row.delivered}</td>
                  <td className="text-right py-2 px-3 tabular-nums">{row.opened}</td>
                  <td className="text-right py-2 px-3 tabular-nums">{row.clicked}</td>
                  <td className="text-right py-2 px-3 tabular-nums">{row.bounced}</td>
                  <td className="text-right py-2 px-3 tabular-nums">{pct(row.open_rate)}</td>
                  <td className="text-right py-2 px-3 tabular-nums">{pct(row.click_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Bounces */}
      {recent_bounces.length > 0 && (
        <div className="bg-slate-800 rounded-xl p-5">
          <h2 className="text-lg font-bold text-white mb-4">Recent Bounces</h2>
          <div className="space-y-2">
            {recent_bounces.map((b, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0">
                <div>
                  <p className="text-sm text-slate-300">{b.email}</p>
                  <p className="text-xs text-slate-500">{b.bounce_reason}</p>
                </div>
                <span className="text-xs text-slate-500">{new Date(b.bounced_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
